import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Play } from 'lucide-react';
import { useUserLibraryForTab } from '@/features/library/hooks/useUserLibrary';
import { useRecommendedBooksInfinite } from '@/features/library/hooks/useRecommendedBooksInfinite';
import RecommendedBookCard from '@/features/library/components/BookCard';

interface ContinueReadingProps {
  onStatusChange?: (status: { hasBooks: boolean; isLoading: boolean }) => void;
}

const toPersianDigits = (s: string) => s.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);

interface BookCardProps {
  bookId: number | string | undefined;
  title: string;
  author: string;
  cover: string;
  progress: number;
  onClick: () => void;
}

const BookCard: React.FC<BookCardProps> = ({ title, author, cover, progress, onClick }) => {
  const [coverColor, setCoverColor] = useState('#64748B');

  useEffect(() => {
    if (!cover) {
      setCoverColor('#64748B');
      return;
    }

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const context = canvas.getContext('2d');
        if (!context) return;
        context.drawImage(image, 0, 0, 1, 1);
        const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;
        setCoverColor(`rgb(${red}, ${green}, ${blue})`);
      } catch {
        // Cross-origin images may not allow canvas sampling; keep the fallback color.
      }
    };
    image.src = cover;

    return () => {
      cancelled = true;
    };
  }, [cover]);

  const isLightCover = (() => {
    const match = coverColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) return false;
    const [, red, green, blue] = match.map(Number);
    return (red * 299 + green * 587 + blue * 114) / 1000 > 160;
  })();
  const foregroundColor = '#111827';
  const mutedForegroundColor = 'rgba(17, 24, 39, 0.72)';

  return (
    <button
      onClick={onClick}
      data-book-card="true"
      className="relative w-full h-[290px] sm:h-[320px] rounded-3xl overflow-hidden ring-1 ring-black/10 transition-all duration-300 group flex-shrink-0 snap-center p-3 sm:p-4 flex flex-col gap-3 text-right"
      style={{ backgroundColor: '#FFFDFC', boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
    >
      {/* Content container */}
      <div className="relative z-10 flex-1 min-h-0 flex items-center gap-3 sm:gap-4 overflow-hidden">
        {/* Book info - centered vertically */}
        <div className="order-2 min-w-0 flex-1 text-right self-center z-20 pr-1" style={{ color: foregroundColor }}>
          {/* Title and Author Group - Very tight spacing */}
          <div className="mb-4 sm:mb-6">
            <h3 className="font-persian font-bold text-lg sm:text-xl md:text-2xl line-clamp-2 drop-shadow-lg leading-tight">
              {title}
            </h3>
            <p className="font-persian text-xs sm:text-sm line-clamp-1 mt-1" style={{ color: mutedForegroundColor }}>
              {author}
            </p>
          </div>

          {/* Play button and progress ring */}
          <div className="flex items-center justify-start gap-3 sm:gap-4">
            {/* Play/Resume button - now first (on the right in RTL) */}
            <div className="hidden">
              <div className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl shadow-lg transition-all duration-200 group-hover:scale-105"
                style={{ backgroundColor: '#ffffff' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
              >
                <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-black" />
                <span className="text-xs sm:text-sm font-bold font-persian text-black">ادامه مطالعه</span>
              </div>
            </div>

            {/* Circular progress ring - now second (on the left in RTL) */}
            <div className="relative flex-shrink-0">
              <svg className="w-16 h-16 sm:w-20 sm:h-20 transform -rotate-90" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="rgba(17, 24, 39, 0.16)"
                  strokeWidth="8"
                />
                {/* Progress circle */}
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="#7063FF"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 42}`}
                  strokeDashoffset={`${2 * Math.PI * 42 * (1 - progress / 100)}`}
                  className="transition-all duration-500"
                />
              </svg>
              {/* Percentage text inside ring */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm sm:text-base font-bold drop-shadow-md">
                  {toPersianDigits(`${progress}٪`)}
                </span>
              </div>
            </div>
          </div>
          <div className="hidden mt-5 sm:mt-6">
            <div className="flex items-center justify-between gap-2 mb-1.5 text-[10px] sm:text-xs font-bold" style={{ color: mutedForegroundColor }}>
              <span className="font-persian">پیشرفت مطالعه</span>
              <span>{toPersianDigits(`${progress}%`)}</span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ backgroundColor: isLightCover ? 'rgba(17, 24, 39, 0.16)' : 'rgba(255, 255, 255, 0.28)' }}
            >
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${progress}%`, backgroundColor: foregroundColor }}
              />
            </div>
          </div>
        </div>

        {/* Book cover is on the right and vertically centered. */}
        <div className="order-1 flex-shrink-0 w-[118px] sm:w-[140px] h-[184px] sm:h-[210px] self-center">
          <div className="w-full h-full rounded-xl overflow-hidden shadow-md ring-0 group-hover:scale-105 transition-transform duration-300 relative">
            {cover ? (
              <img
                src={cover}
                alt={title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-5xl">
                📚
              </div>
            )}
            {/* 3D spine effect */}
          </div>
        </div>
      </div>

      {/* Full-width resume button below the cover and information. */}
      <div
        className="relative z-10 mx-1.5 flex items-center justify-center gap-2 rounded-2xl px-4 py-3.5 shadow-none transition-all duration-200 group-hover:brightness-110"
        style={{ backgroundColor: '#7063FF', color: '#FFFFFF' }}
      >
        <span className="text-xs sm:text-sm font-bold font-persian">ادامه مطالعه</span>
        <ChevronLeft className="relative -top-0.5 w-4 h-4" />
      </div>
    </button>
  );
};

const ContinueReading: React.FC<ContinueReadingProps> = ({ onStatusChange }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useUserLibraryForTab('in-progress');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const items = data?.items || [];
  const maxVisibleDots = 5;

  // Map items to book data
  const books = items.map((book) => ({
    id: book.id,
    bookId: book.book?.book_id,
    title: book.book?.title || 'بدون عنوان',
    author: book.book?.author || 'نویسنده نامشخص',
    cover: book.book?.cover_image_url ?? '',
    progress: Math.max(0, Math.min(100, Number(book.progress ?? 0))),
  }));

  const booksCount = books.length;
  const {
    books: recommendedBooks,
    isLoading: recommendationsLoading,
    isFetchingNextPage: recommendationsFetchingNextPage,
    hasNextPage: recommendationsHasNextPage,
    fetchNextPage: fetchRecommendedNextPage,
  } = useRecommendedBooksInfinite({
    enabled: !isLoading && books.length === 0,
    limit: 10,
  });

  useEffect(() => {
    onStatusChange?.({
      hasBooks: books.length > 0,
      isLoading: isLoading && !data,
    });
  }, [books.length, data, isLoading, onStatusChange]);

  const handleRecommendedScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (
        recommendationsFetchingNextPage ||
        !recommendationsHasNextPage
      ) {
        return;
      }

      const container = event.currentTarget;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const currentScroll = Math.abs(container.scrollLeft);
      if (maxScroll > 0 && currentScroll >= maxScroll * 0.8) {
        fetchRecommendedNextPage();
      }
    },
    [
      fetchRecommendedNextPage,
      recommendationsFetchingNextPage,
      recommendationsHasNextPage,
    ]
  );

  // Scroll handler - find which item is most visible using scroll position
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || booksCount === 0) return;
    
    // Calculate index based on scroll position
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.scrollWidth / booksCount;
    
    // For RTL, scrollLeft is negative or we need to calculate from the right
    // Use absolute scroll position
    const absScroll = Math.abs(scrollLeft);
    const calculatedIndex = Math.round(absScroll / itemWidth);
    const newIndex = Math.max(0, Math.min(booksCount - 1, calculatedIndex));
    
    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  }, [booksCount, activeIndex]);

  // Attach scroll listener - only when booksCount changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || booksCount === 0) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    
    // Initial check after mount
    handleScroll();
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [booksCount, handleScroll]);

  // Scroll to specific index when dot is clicked
  const scrollToIndex = useCallback((index: number) => {
    const container = scrollContainerRef.current;
    if (!container || index < 0 || index >= booksCount) return;
    
    const cards = Array.from(container.querySelectorAll<HTMLElement>('[data-book-card]'));
    if (cards[index]) {
      cards[index].scrollIntoView({ 
        behavior: 'smooth', 
        inline: 'center', 
        block: 'nearest' 
      });
    }
  }, [booksCount]);

  if (isLoading) {
    return (
      <div className="h-[200px] sm:h-[220px] rounded-2xl bg-gray-200 animate-pulse" />
    );
  }

  if (!books.length) {
    return (
      <div>
        {recommendationsLoading ? (
          <div
            className="grid grid-flow-col grid-rows-2 auto-cols-[7.25rem] gap-x-3 gap-y-4 overflow-hidden px-1"
            style={{ gridTemplateRows: 'repeat(2, minmax(0, 1fr))' }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="w-[7.25rem]">
                <div className="h-[170px] w-full rounded-xl bg-gray-200 animate-pulse mb-2" />
                <div className="h-3 w-full rounded bg-gray-200 animate-pulse mb-1" />
                <div className="h-3 w-2/3 rounded bg-gray-200 animate-pulse" />
              </div>
            ))}
          </div>
        ) : recommendedBooks.length > 0 ? (
          <div
            className="grid grid-flow-col grid-rows-2 auto-cols-[7.25rem] gap-x-3 gap-y-4 overflow-x-auto scrollbar-hide pb-2 px-1"
            onScroll={handleRecommendedScroll}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
            }}
          >
            {recommendedBooks.map((book, index) => (
              <div key={`${book.book_id}-${index}`} className="w-[7.25rem] min-w-0">
                <RecommendedBookCard
                  book={book}
                  titleClassName="text-xs leading-tight"
                  coverClassName="!aspect-auto !h-[170px]"
                  onClick={() =>
                    navigate('/library/book', {
                      state: {
                        bookId: book.book_id,
                        source: 'recommended',
                        books: recommendedBooks,
                        initialIndex: index,
                      },
                    })
                  }
                />
              </div>
            ))}
            {recommendationsFetchingNextPage && (
              <div className="w-[7.25rem] min-w-0">
                <div className="h-[170px] w-full rounded-xl bg-gray-200 animate-pulse mb-2" />
                <div className="h-3 w-full rounded bg-gray-200 animate-pulse mb-1" />
                <div className="h-3 w-2/3 rounded bg-gray-200 animate-pulse" />
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl bg-white shadow-sm px-4 py-6 text-center font-persian text-sm text-gray-600">
            هنوز کتاب پیشنهادی‌ای پیدا نشد.
          </div>
        )}
      </div>
    );
  }

  // Single book - no scroll needed
  if (books.length === 1) {
    const bookData = books[0];
    return (
      <BookCard
        bookId={bookData.bookId}
        title={bookData.title}
        author={bookData.author}
        cover={bookData.cover}
        progress={bookData.progress}
        onClick={() => navigate(`/learning/path`, { state: { bookId: bookData.bookId } })}
      />
    );
  }

  // Multiple books - show horizontal scroll with dots
  // Instagram style: max 5 dots visible, window follows active dot
  const totalBooks = books.length;

  // Calculate visible dots - simple sliding window centered on active
  const getVisibleDots = (): { index: number; size: 'tiny' | 'small' | 'normal' }[] => {
    // If total books <= maxVisibleDots, show all as normal
    if (totalBooks <= maxVisibleDots) {
      return Array.from({ length: totalBooks }, (_, i) => ({ 
        index: i, 
        size: 'normal' as const 
      }));
    }

    // Calculate window start - keep active in center when possible
    const halfWindow = Math.floor(maxVisibleDots / 2); // 2
    let windowStart: number;

    if (activeIndex <= halfWindow) {
      // Near start - window starts at 0
      windowStart = 0;
    } else if (activeIndex >= totalBooks - 1 - halfWindow) {
      // Near end - window ends at last item
      windowStart = totalBooks - maxVisibleDots;
    } else {
      // Middle - center on active
      windowStart = activeIndex - halfWindow;
    }

    const windowEnd = windowStart + maxVisibleDots - 1;
    const hasMoreBefore = windowStart > 0;
    const hasMoreAfter = windowEnd < totalBooks - 1;

    const dots: { index: number; size: 'tiny' | 'small' | 'normal' }[] = [];

    for (let i = 0; i < maxVisibleDots; i++) {
      const dotIndex = windowStart + i;
      let size: 'tiny' | 'small' | 'normal' = 'normal';

      // First dot is small/tiny if there are more before
      if (i === 0 && hasMoreBefore) {
        size = 'small';
      }
      // Last dot is small/tiny if there are more after
      else if (i === maxVisibleDots - 1 && hasMoreAfter) {
        size = 'small';
      }

      dots.push({ index: dotIndex, size });
    }

    return dots;
  };

  const visibleDots = getVisibleDots();

  // Get dot classes based on whether it's active and its size
  const getDotClasses = (dotIndex: number, size: 'tiny' | 'small' | 'normal') => {
    const isActive = dotIndex === activeIndex;
    const baseClasses = 'rounded-full transition-all duration-200';
    
    if (isActive) {
      return `${baseClasses} bg-purple-600 w-5 h-2`;
    }
    
    if (size === 'tiny') {
      return `${baseClasses} bg-gray-300 w-1 h-1`;
    }
    
    if (size === 'small') {
      return `${baseClasses} bg-gray-300 w-1.5 h-1.5`;
    }
    
    return `${baseClasses} bg-gray-300 hover:bg-gray-400 w-2 h-2`;
  };

  return (
    <div className="space-y-3">
      <div
        ref={scrollContainerRef}
        className="flex w-full gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide p-3"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {books.map((bookData, index) => (
          <BookCard
            key={bookData.id || index}
            bookId={bookData.bookId}
            title={bookData.title}
            author={bookData.author}
            cover={bookData.cover}
            progress={bookData.progress}
            onClick={() => navigate(`/learning/path`, { state: { bookId: bookData.bookId } })}
          />
        ))}
      </div>

      {/* Dot indicators - Instagram style */}
      <div className="flex items-center justify-center gap-1.5">
        {visibleDots.map(({ index, size }) => (
          <button
            key={index}
            onClick={() => scrollToIndex(index)}
            className={getDotClasses(index, size)}
            aria-label={`رفتن به کتاب ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ContinueReading;
